/**
 * Floating combat text settings: text scale, party numbers, crit pop, full numbers.
 * Persisted in localStorage (separately from graphics settings); every storage access is in try/catch.
 * `combatTextSettings` is a tiny shared store: the settings panel writes it, FloatingText subscribes.
 */
import { el } from './dom';

export interface CombatTextSettings {
  /** Text size multiplier (0.75..1.75). */
  scale: number;
  /** Also show the rest of the party's outgoing numbers (smaller, dimmer). */
  showParty: boolean;
  /** Crits pop in with a scale overshoot. */
  critPop: boolean;
  /** +castux: full numbers ("12,400") instead of abbreviated ones ("12.4K"). */
  fullNumbers: boolean;
}

export const DEFAULT_COMBAT_TEXT: Readonly<CombatTextSettings> = Object.freeze({ scale: 1, showParty: false, critPop: true, fullNumbers: false });
export const COMBAT_TEXT_KEY = 'keystone.combatText.v1';
export const COMBAT_TEXT_SCALE = { min: 0.75, max: 1.75, step: 0.05 } as const;

export function sanitizeCombatText(raw: unknown): CombatTextSettings {
  const s: CombatTextSettings = { ...DEFAULT_COMBAT_TEXT };
  if (!raw || typeof raw !== 'object') return s;
  const r = raw as Record<string, unknown>;
  const scale = r['scale'];
  if (typeof scale === 'number' && Number.isFinite(scale)) s.scale = Math.min(COMBAT_TEXT_SCALE.max, Math.max(COMBAT_TEXT_SCALE.min, scale));
  if (typeof r['showParty'] === 'boolean') s.showParty = r['showParty'];
  if (typeof r['critPop'] === 'boolean') s.critPop = r['critPop'];
  if (typeof r['fullNumbers'] === 'boolean') s.fullNumbers = r['fullNumbers']; // +castux
  return s;
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadCombatText(storage: Pick<Storage, 'getItem'> | null = defaultStorage()): CombatTextSettings {
  try {
    const raw = storage?.getItem(COMBAT_TEXT_KEY);
    return sanitizeCombatText(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_COMBAT_TEXT };
  }
}

/** Returns false when the write failed (storage blocked or full). */
export function saveCombatText(s: CombatTextSettings, storage: Pick<Storage, 'setItem'> | null = defaultStorage()): boolean {
  try {
    if (!storage) return false;
    storage.setItem(COMBAT_TEXT_KEY, JSON.stringify(sanitizeCombatText(s)));
    return true;
  } catch {
    return false;
  }
}

type Listener = (s: Readonly<CombatTextSettings>) => void;

/** Shared, lazily loaded store. */
class CombatTextStore {
  private value: CombatTextSettings | null = null;
  private readonly listeners = new Set<Listener>();

  get(): Readonly<CombatTextSettings> {
    return (this.value ??= loadCombatText());
  }

  set(patch: Partial<CombatTextSettings>): void {
    this.value = sanitizeCombatText({ ...this.get(), ...patch });
    saveCombatText(this.value);
    for (const l of this.listeners) l(this.value);
  }

  /** Calls `fn` now and on every change. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.get());
    return () => this.listeners.delete(fn);
  }
}

export const combatTextSettings = new CombatTextStore();

/** Adds a "Combat text" section to the settings panel. Returns a sync function (re-reads the store into the controls). */
export function mountCombatTextControls(panel: HTMLElement, before: Node | null = null): () => void {
  const section = el('div', 'ct-settings');
  panel.insertBefore(section, before);
  el('div', 'gfx-title', section, 'Combat text');
  const row = (label: string): HTMLElement => {
    const r = el('label', 'gfx-row', section);
    el('span', '', r, label);
    return r;
  };

  const sizeRow = row('Text size');
  const out = el('output', 'gfx-val', sizeRow);
  const scale = el('input', '', sizeRow);
  scale.type = 'range';
  scale.min = String(COMBAT_TEXT_SCALE.min);
  scale.max = String(COMBAT_TEXT_SCALE.max);
  scale.step = String(COMBAT_TEXT_SCALE.step);
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  scale.addEventListener('input', () => {
    out.textContent = pct(Number(scale.value));
    combatTextSettings.set({ scale: Number(scale.value) });
  });

  const check = (label: string, key: 'showParty' | 'critPop' | 'fullNumbers'): HTMLInputElement => {
    const r = row(label);
    r.classList.add('check');
    const input = el('input', '', r);
    input.type = 'checkbox';
    input.addEventListener('change', () => combatTextSettings.set({ [key]: input.checked }));
    return input;
  };
  const party = check("Show party's numbers", 'showParty');
  const pop = check('Crit pop', 'critPop');
  const full = check('Full numbers (no 12.4K)', 'fullNumbers'); // +castux

  return () => {
    const s = combatTextSettings.get();
    scale.value = String(s.scale);
    out.textContent = pct(s.scale);
    party.checked = s.showParty;
    pop.checked = s.critPop;
    full.checked = s.fullNumbers;
  };
}
